import { INTENT_LIMITS, newAcceptanceCriterion, type AcceptanceCriterion } from '@blueprint/domain'
import { Button, IconButton, TextInput } from '../../../components/controls.js'
import styles from './intent.module.css'

interface Props {
  criteria: readonly AcceptanceCriterion[]
  onChange: (next: AcceptanceCriterion[]) => void
}

/**
 * Ordered, editable criteria. These describe observable success; they are not
 * task-completion checkboxes in this increment.
 */
export function AcceptanceCriteriaEditor({ criteria, onChange }: Props) {
  const update = (id: string, text: string) =>
    onChange(criteria.map((criterion) => (criterion.id === id ? { ...criterion, text } : criterion)))

  const move = (index: number, delta: -1 | 1) => {
    const target = index + delta
    if (target < 0 || target >= criteria.length) return
    const next = [...criteria]
    const [moved] = next.splice(index, 1)
    if (!moved) return
    next.splice(target, 0, moved)
    onChange(next)
  }

  return (
    <div className={styles.criteria}>
      {criteria.length === 0 ? (
        <p className={styles.empty}>
          No acceptance criteria yet. Each one should describe an observable result that establishes success.
        </p>
      ) : null}

      {criteria.map((criterion, index) => (
        <div key={criterion.id} className={styles.criterion}>
          <span className={styles.criterionIndex} aria-hidden="true">
            {index + 1}.
          </span>
          <TextInput
            value={criterion.text}
            aria-label={`Acceptance criterion ${index + 1}`}
            maxLength={INTENT_LIMITS.criterion}
            placeholder="An observable result that establishes success"
            onChange={(event) => update(criterion.id, event.target.value)}
          />
          <IconButton
            aria-label={`Move acceptance criterion ${index + 1} up`}
            disabled={index === 0}
            onClick={() => move(index, -1)}
          >
            &#9650;
          </IconButton>
          <IconButton
            aria-label={`Move acceptance criterion ${index + 1} down`}
            disabled={index === criteria.length - 1}
            onClick={() => move(index, 1)}
          >
            &#9660;
          </IconButton>
          <IconButton
            danger
            aria-label={`Remove acceptance criterion ${index + 1}`}
            onClick={() => onChange(criteria.filter((item) => item.id !== criterion.id))}
          >
            &#10005;
          </IconButton>
        </div>
      ))}

      <div>
        <Button
          disabled={criteria.length >= INTENT_LIMITS.criteriaCount}
          onClick={() => onChange([...criteria, newAcceptanceCriterion()])}
        >
          Add criterion
        </Button>
      </div>
    </div>
  )
}
